import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import {
    exportSalaryReport,
    downloadSupplierInvoiceFiles,
    downloadReceiptsZip,
    downloadCustomerInvoiceFiles,
    downloadCostCentresReport,
} from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Download, Mail } from 'lucide-react';
import EmailReportsDialog from '../../components/admin/EmailReportsDialog';
import MonthNavigator from '../../components/month-navigator';

const XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Månadsrapporter — the one place the month's paperwork leaves for the
 * accountant. Customer invoices, salaries, expenses, cost centres and supplier invoices
 * used to be downloaded/emailed from the top of the Billing and Salary pages
 * separately; they are collected here so the month-end send is one action.
 */
export default function MonthlyReportsPage() {
    const { t } = useTranslation();
    // POST /reports/email is require_admin; accountants can only download.
    const { isAdmin } = useAuth();
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());
    const [emailDialogOpen, setEmailDialogOpen] = useState(false);
    const [downloading, setDownloading] = useState(null);

    const mm = String(month).padStart(2, '0');
    const downloads = [
        { key: 'customer_invoices', label: t('email_dialog.include_customer_invoices'), fetch: () => downloadCustomerInvoiceFiles(year, month), type: 'application/zip', name: `customer-invoices-${year}-${mm}.zip` },
        { key: 'salary', label: t('email_dialog.include_salary'), fetch: () => exportSalaryReport(month, year), type: XLSX, name: `salary_report_${year}_${mm}.xlsx` },
        { key: 'receipts', label: t('email_dialog.include_receipts'), fetch: () => downloadReceiptsZip(year, month), type: 'application/zip', name: `receipts-${year}-${mm}.zip` },
        { key: 'cost_centres', label: t('email_dialog.include_cost_centres'), fetch: () => downloadCostCentresReport(year, month), type: XLSX, name: `cost_centres_${year}_${mm}.xlsx` },
        { key: 'supplier_files', label: t('email_dialog.include_supplier_invoices'), fetch: () => downloadSupplierInvoiceFiles(year, month), type: 'application/zip', name: `supplier-invoice-files-${year}-${mm}.zip` },
    ];

    const handleDownload = async (d) => {
        setDownloading(d.key);
        try {
            const response = await d.fetch();
            const url = window.URL.createObjectURL(new Blob([response.data], { type: d.type }));
            const a = document.createElement('a');
            a.href = url;
            a.download = d.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            toast.success(t('reports.export_success'));
        } catch {
            toast.error(t('reports.failed_export'));
        } finally {
            setDownloading(null);
        }
    };

    const shiftMonth = (delta) => {
        const d = new Date(year, month - 1 + delta, 1);
        setMonth(d.getMonth() + 1);
        setYear(d.getFullYear());
    };

    return (
        <div className="space-y-6" data-testid="monthly-reports-page">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
                <div>
                    <h1 className="font-heading text-4xl font-bold tracking-tight">{t('monthly_reports.title')}</h1>
                    <p className="text-muted-foreground mt-1">{t('monthly_reports.subtitle')}</p>
                </div>
                {isAdmin && (
                    <Button
                        className="uppercase tracking-wider"
                        onClick={() => setEmailDialogOpen(true)}
                        data-testid="email-reports-btn"
                    >
                        <Mail className="w-4 h-4 mr-2" />
                        {t('monthly_reports.send_button')}
                    </Button>
                )}
            </div>

            <MonthNavigator
                year={year}
                month={month}
                onPrev={() => shiftMonth(-1)}
                onNext={() => shiftMonth(1)}
            />

            <Card>
                <CardContent className="p-0 divide-y divide-border">
                    {downloads.map((d) => (
                        <div key={d.key} className="flex items-center justify-between gap-3 p-4">
                            <span>{d.label}</span>
                            <Button
                                variant="outline"
                                className="uppercase tracking-wider"
                                onClick={() => handleDownload(d)}
                                disabled={downloading !== null}
                                data-testid={`download-${d.key}-btn`}
                            >
                                <Download className="w-4 h-4 mr-2" />
                                {downloading === d.key ? t('common.exporting') : t('common.download')}
                            </Button>
                        </div>
                    ))}
                </CardContent>
            </Card>

            <EmailReportsDialog
                open={emailDialogOpen}
                onOpenChange={setEmailDialogOpen}
                defaultMonth={month}
                defaultYear={year}
            />
        </div>
    );
}
